// ---- Fishing (client) --------------------------------------------------------
// The fourth focus.ts-sibling activity, beside combat.ts (fight), talk.ts
// (converse), and interact.ts (props). Two loops (Living Waters rework):
//
//   ANGLING (active): with a rod on the held-tool channel, click water (or the
//   Cast slot) → `cast` intent → game.fishing locks movement and the camera
//   eases into the first-person shot down the cast line (camera.ts
//   updateFishingCamera, driven from game.ts like the talk cutscene). The
//   SERVER simulates the water's fish (visible as shadows — src/fish.ts) and
//   runs the encounter: `nibble` tugs the bobber (pulling now never hooks and
//   may spook), `bite` arms the "fish.reel" grab-window sweep — pull (Space /
//   click / the slot) inside it to HOOK. `fishOn` opens the reel-in MINIGAME
//   (src/minigame/reel.ts — an isolated, swappable module; we stream per-arrow
//   hit/miss and the server owns the verdict). `fishLost` returns to waiting
//   (the cast outlives the fish); `catch`/`escape` resolve the cast and the
//   camera eases back out (game.fishingExit).
//
//   NETTING (passive): with a net in the bag, near the water's edge, the Set Net
//   slot anchors it (`deployNet`); the server owns the durable soak timer and
//   everyone sees the floats (the `nets`/`net` mirror below). Return once ready
//   and the same slot reads Collect. No camera change, no input lock — relaxed.
//
// Wiring rule (game.ts): initFishing runs AFTER initInteract and BEFORE
// initFocus — its pointerdown must win the canvas click while a line is out
// (stopImmediatePropagation), and its Esc must cancel the cast before focus.ts
// would drop a (already-cleared) focus.

import * as THREE from "three";
import { game, inputLocked } from "./state.ts";
import { clearFocus, getFocus, nearestFocusableDistance } from "./focus.ts";
import { heightAt, WATER_LEVEL } from "./terrain.ts";
import { toonMat } from "./toon.ts";
import { itemSpec, itemSpecOrNull, ITEMS } from "./items/index.ts";
import { isNet } from "./items/define.ts";
import { equippedRod, equippedRodType, countOf, flashToast } from "./inventory.ts";
import { sendCast, sendReel, sendReelInput, sendCastEnd, sendDeployNet, sendCollectNet, sendKeepCatch } from "./net.ts";
import { startCooldown } from "./cooldown.ts";
import { hasBoxUnlocked } from "./skill.ts";
import { SKILLS, fishingSum } from "./skill/index.ts";
import { resetFishingCamera } from "./camera.ts";
import { WATER_MARGIN, genReelPattern } from "./sim/fishing.ts";
import { makeReelMinigame } from "./minigame/reel.ts";
import type { Minigame } from "./minigame/types.ts";
import { FISHING_TUNING as FT } from "./tunables.ts";
import type { ItemType, NetDeploy } from "./shared.ts";
import { registerHoverProbe } from "./cursor.ts";

/** Cooldown key for the bite grab window — its shrinking sweep on the Cast/
 *  Pull slot IS the visible reaction timer (armed on `bite`). */
export const REEL_CD = "fish.reel";

const FISHING_EXIT_DUR = 0.5; // camera ease-back window (mirrors talkExit)
const PULL_DEBOUNCE = 300;    // ms between pull intents (the server gates too)

let scene: THREE.Scene;
let camera: THREE.Camera;

type Phase = "idle" | "cast" | "biting" | "hooked";
let phase: Phase = "idle";
let minigame: Minigame | null = null; // live only while hooked (the swappable seam)
let tugT = 0;                      // seconds of nibble-tug FX left on the bobber
let lastPull = 0;                  // performance.now() of the last sent pull
const castPt = { x: 0, z: 0 };     // where the line is out (drives bobber + camera)

const raycaster = new THREE.Raycaster();
const ndc = new THREE.Vector2();

let promptEl: HTMLElement | null = null;
let promptShown = "";

// ---- The bobber (client-local FX — only the caster stares at their float) ----
let bobber: THREE.Group | null = null;

function makeBobber(): THREE.Group {
  const g = new THREE.Group();
  const top = new THREE.Mesh(new THREE.SphereGeometry(0.09, 10, 8), toonMat(0xd23c2a));
  top.position.y = 0.07;
  const bottom = new THREE.Mesh(new THREE.SphereGeometry(0.09, 10, 8), toonMat(0xf0ead8));
  bottom.position.y = -0.02;
  g.add(top, bottom);
  return g;
}

// ---- Remote casters' bobbers (net.ts drives these from the snapshot) ----------
// Everyone sees a float where another player's line is out. Keyed by player id.
// The LOCAL caster keeps their own richer `bobber` above (nibble/bite/hooked FX);
// these are the resting floats for OTHER anglers, placed from NetPlayer.cx/cz and
// given only a gentle idle bob (no per-remote encounter state rides the wire).
const remoteBobbers = new Map<string, THREE.Group>();

/** net.ts: a remote is fishing with their line at (x,z) — show/move their float. */
export function setRemoteBobber(id: string, x: number, z: number): void {
  let g = remoteBobbers.get(id);
  if (!g) { g = makeBobber(); scene.add(g); remoteBobbers.set(id, g); }
  g.position.set(x, WATER_LEVEL + 0.05, z);
}

/** net.ts: a remote stopped fishing / left interest / disconnected — drop it. */
export function clearRemoteBobber(id: string): void {
  const g = remoteBobbers.get(id);
  if (g) scene.remove(g);
  remoteBobbers.delete(id);
}

// ---- Deployed-net mirror (server-owned; everyone sees the floats) -------------
const nets = new Map<string, NetDeploy>();
const netGroups = new Map<string, THREE.Group>();

function makeNetFloats(mine: boolean): THREE.Group {
  const g = new THREE.Group();
  const cork = toonMat(mine ? 0xd8a44a : 0x7a4a2b);
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    const m = new THREE.Mesh(new THREE.SphereGeometry(0.09, 8, 6), cork);
    m.position.set(Math.sin(a) * 0.7, 0, Math.cos(a) * 0.7);
    g.add(m);
  }
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.7, 0.025, 6, 20), toonMat(0xc8b98a));
  ring.rotation.x = Math.PI / 2;
  g.add(ring);
  return g;
}

function addNetGroup(n: NetDeploy): void {
  const g = makeNetFloats(!!n.mine);
  g.position.set(n.x, WATER_LEVEL + 0.04, n.z);
  scene.add(g);
  netGroups.set(n.id, g);
}

function removeNetGroup(id: string): void {
  const g = netGroups.get(id);
  if (g) scene.remove(g);
  netGroups.delete(id);
}

/** Full sync (join/reconnect): replace the whole mirror. */
export function setNets(list: NetDeploy[]): void {
  for (const id of [...netGroups.keys()]) removeNetGroup(id);
  nets.clear();
  for (const n of list) {
    nets.set(n.id, n);
    addNetGroup(n);
  }
}

/** One net deployed/collected (broadcast, personalized `mine`). */
export function applyNetDelta(op: "add" | "remove", net?: NetDeploy, id?: string): void {
  if (op === "add" && net) {
    nets.set(net.id, net);
    removeNetGroup(net.id); // belt-and-braces against a replayed add
    addNetGroup(net);
  } else if (op === "remove" && id) {
    nets.delete(id);
    removeNetGroup(id);
  }
}

// ---- Shared little helpers ----------------------------------------------------

/** "Some other activity owns the input" for this module's slot states + actions
 *  (game.fishing itself is checked separately where the branching differs — the
 *  shared predicate includes it, but every caller guards game.fishing first). */
function busy(): boolean {
  return inputLocked();
}

/** Castable water: genuinely below the waterline (rejects shore "bleed"). */
function isWater(x: number, z: number): boolean {
  return heightAt(x, z) < WATER_LEVEL - WATER_MARGIN;
}

/** The folded fishing box effects (reel window etc.) — THE shared fold
 *  (skill/define.ts), over the mirrored unlocked boxes. Pure UX; the server
 *  re-derives its own. */
function boxFx() {
  return fishingSum(SKILLS, hasBoxUnlocked);
}

function rodCastRange(): number {
  return equippedRod()?.castRange ?? FT.castRange;
}

/** First water point straight ahead of the player within `max` reach, or null.
 *  The slot ACTIONS use this fresh (it aims the real cast/deploy); the per-frame
 *  slot states go through the cached probe below. */
function forwardWaterPoint(max: number): { x: number; z: number } | null {
  const p = game.player.position;
  const ry = game.player.rotation.y;
  const dx = Math.sin(ry), dz = Math.cos(ry);
  for (let d = 3; d <= max; d += 1.5) {
    const x = p.x + dx * d, z = p.z + dz * d;
    if (isWater(x, z)) return { x, z };
  }
  return null;
}

// The probe costs several heightAt() samples (4-octave noise) and the slot
// states ask EVERY frame whenever a rod/net is carried — so cache per reach
// with a short TTL, invalidated by moving or turning. Button eligibility
// doesn't need 60Hz precision; this keeps the noise-fn budget near zero while
// standing still.
type WaterProbe = { at: number; x: number; z: number; ry: number; result: { x: number; z: number } | null };
const waterProbes = new Map<number, WaterProbe>();
function cachedForwardWater(max: number): { x: number; z: number } | null {
  const now = performance.now();
  const p = game.player.position, ry = game.player.rotation.y;
  const c = waterProbes.get(max);
  if (c && now - c.at < 200 && Math.abs(ry - c.ry) < 0.1 && Math.hypot(p.x - c.x, p.z - c.z) < 0.5) {
    return c.result;
  }
  const result = forwardWaterPoint(max);
  waterProbes.set(max, { at: now, x: p.x, z: p.z, ry, result });
  return result;
}

// The deployable net types, resolved once — content is static, so the per-frame
// check only scans the bag (countOf), not the whole item catalog.
let netTypes: ItemType[] | null = null;

/** Any deployable net we're carrying (first match — CP1 has one net type). */
function carriedNetType(): ItemType | null {
  netTypes ??= ITEMS.filter(isNet).map((s) => s.type);
  for (const type of netTypes) {
    if (countOf(type) > 0) return type;
  }
  return null;
}

/** The nearest of OUR deployed nets within reach, or null. */
function nearestOwnNet(): NetDeploy | null {
  const p = game.player.position;
  let best: NetDeploy | null = null, bd = Infinity;
  for (const n of nets.values()) {
    if (!n.mine) continue;
    const d = Math.hypot(p.x - n.x, p.z - n.z);
    if (d <= FT.netRange && d < bd) { bd = d; best = n; }
  }
  return best;
}

// ---- The angling loop -----------------------------------------------------------

function castAt(x: number, z: number): void {
  clearFocus(); // the first-person camera replaces any target lock
  castPt.x = x;
  castPt.z = z;
  phase = "cast";
  tugT = 0;
  game.fishing = true;
  game.player.userData.fishing = true; // both hands on the rod (updatePlayer pose)
  resetFishingCamera(); // ease in from the live view (game.ts drives the camera)
  if (bobber) {
    bobber.visible = true;
    bobber.position.set(x, WATER_LEVEL + 0.05, z);
  }
  sendCast(x, z);
}

/** Validate + cast at a clicked/aimed water point (both entries funnel here). */
function tryCastAt(x: number, z: number): void {
  const p = game.player.position;
  if (Math.hypot(p.x - x, p.z - z) > rodCastRange()) {
    flashToast("Too far to cast");
    return;
  }
  if (!isWater(x, z)) {
    flashToast("Too shallow — find open water");
    return;
  }
  castAt(x, z);
}

/** THE PULL — a jerk on the line. Inside the bite window it hooks; during a
 *  nibble it never hooks (and may spook); earlier it's a twitch. Repeatable by
 *  design (the old one-shot `awaiting` latch is gone) — the server judges every
 *  pull, we just debounce so a click flurry doesn't spam intents. */
function pull(): void {
  if (!game.fishing || phase === "hooked") return; // the minigame owns input once hooked
  const now = performance.now();
  if (now - lastPull < PULL_DEBOUNCE) return;
  lastPull = now;
  sendReel();
}

function cancelCast(): void {
  sendCastEnd();
  endFishing();
}

function endFishing(): void {
  if (!game.fishing) return;
  phase = "idle";
  tugT = 0;
  minigame?.unmount();
  minigame = null;
  game.fishing = false;
  game.player.userData.fishing = false; // release the two-handed rod pose
  game.fishingExit = FISHING_EXIT_DUR; // ease the camera back (game.ts)
  if (bobber) bobber.visible = false;
  showPrompt("");
}

/** Where the line is out — game.ts aims the first-person camera down this. */
export function getCastPoint(): { x: number; z: number } {
  return castPt;
}

/** Map swap (plan 24, game.ts swapWorld): drop every local fishing state —
 *  line, catch modal, camera ease — without server intents (transferCharacter
 *  already banked an undecided catch + ended the cast server-side). */
export function resetFishing(): void {
  endFishing(); // no-op when no line is out
  for (const id of [...remoteBobbers.keys()]) clearRemoteBobber(id); // drop other anglers' floats too
  catchGroups = [];
  game.catchOpen = false;
  if (catchPanel) catchPanel.style.display = "none";
  game.fishingExit = 0; // no cross-map exit ease — the swap cuts through a fade
}

// ---- Server appliers (called by net.ts) ------------------------------------------

/** A nibble tug (owner-only): the fish is testing the bait — dip the bobber,
 *  DON'T pull (a nibble pull never hooks and may spook it). */
export function applyNibble(): void {
  if (!game.fishing) return;
  tugT = 0.45;
  showPrompt("…something's nibbling — hold steady");
}

/** The bite landed (owner-only): arm the visible grab window + plunge the float.
 *  Window length mirrors the server's (tunable × box reelWindowMul) — the same
 *  client-derived-window pattern as before. */
export function applyBite(): void {
  if (!game.fishing) return;
  phase = "biting";
  startCooldown(REEL_CD, FT.biteWindow * boxFx().reelWindowMul);
}

/** HOOKED (owner-only): both sides expand the SAME arrow pattern from the
 *  server's seed; mount the reel-in minigame (the isolated, swappable module).
 *  Our per-arrow hit/miss stream feeds the server's authoritative judge — the
 *  verdict arrives as the ordinary catch/escape. */
export function applyFishOn(seed: number, fishType: ItemType): void {
  if (!game.fishing) return;
  phase = "hooked";
  tugT = 0;
  const spec = itemSpecOrNull(fishType)?.fish;
  const pattern = genReelPattern(seed, { tier: spec?.tier ?? 0, fightMul: spec?.fightMul }, FT);
  minigame?.unmount();
  minigame = makeReelMinigame({
    pattern,
    hitWindow: FT.reelHitWindow * boxFx().reelWindowMul,
    tun: FT,
    ev: { onInput: (i, hit) => sendReelInput(i, hit) },
  });
  minigame.mount();
  showPrompt(""); // the minigame overlay carries its own title header now
}

/** The fish left (owner-only) — the CAST stays live; back to waiting. */
export function applyFishLost(why: "spooked" | "interest"): void {
  if (!game.fishing) return;
  phase = "cast";
  tugT = 0;
  flashToast(why === "spooked" ? "It spooked and bolted!" : "It lost interest…");
}

export function applyCatch(items: ItemType[], kind: "angling" | "netting"): void {
  endFishing(); // release the angling camera + minigame (no-op for a net haul — never fishing)
  openCatch(items, kind);
}

export function applyEscape(reason?: string): void {
  if (!game.fishing) return;
  if (reason === "escaped") flashToast("It broke free!");
  else if (reason !== "invalid") flashToast("It got away…");
  endFishing();
}

// ---- Catch-preview HUD (what you landed → keep / throw back) -----------------
// A landed catch (one fish angling, the bulk roll netting) is held server-side
// until we answer `keepCatch`. This modal lists the haul grouped by type; each
// row toggles keep/throw-back, "Keep all" banks everything, and confirming sends
// only the kept TYPES (the server throws the rest back). game.catchOpen locks the
// world while it's up (mirrors game.journalOpen). Esc = keep all (never lose a
// catch to a stray key).
let catchPanel: HTMLElement | null = null;
let catchListEl: HTMLElement | null = null;
let catchTitleEl: HTMLElement | null = null;
let catchConfirmBtn: HTMLButtonElement | null = null;
type CatchGroup = { type: ItemType; count: number; keep: boolean };
let catchGroups: CatchGroup[] = [];

function openCatch(items: ItemType[], kind: "angling" | "netting"): void {
  if (!items.length || !catchPanel) return;
  // Group by type, preserving first-seen order (keep/throw-back is per type).
  const order: ItemType[] = [];
  const counts = new Map<ItemType, number>();
  for (const it of items) {
    if (!counts.has(it)) order.push(it);
    counts.set(it, (counts.get(it) ?? 0) + 1);
  }
  catchGroups = order.map((type) => ({ type, count: counts.get(type)!, keep: true }));
  if (catchTitleEl) catchTitleEl.textContent = kind === "netting" ? "Hauled from your net" : "You caught";
  game.catchOpen = true;
  catchPanel.style.display = "flex";
  renderCatch();
}

function renderCatch(): void {
  if (!catchListEl) return;
  catchListEl.replaceChildren();
  for (const g of catchGroups) {
    const spec = itemSpec(g.type);
    const row = document.createElement("button");
    row.type = "button";
    row.className = "catch-item" + (g.keep ? "" : " toss");
    row.innerHTML =
      `<span class="catch-icon">${spec.icon}</span>` +
      `<span class="catch-name">${spec.name}` +
      (g.count > 1 ? ` <span class="catch-x">×${g.count}</span>` : "") +
      `</span>` +
      `<span class="catch-tag">${g.keep ? "Keep" : "Throw back"}</span>`;
    row.addEventListener("click", () => { g.keep = !g.keep; renderCatch(); });
    catchListEl.appendChild(row);
  }
  if (catchConfirmBtn) {
    const kept = catchGroups.reduce((n, g) => n + (g.keep ? g.count : 0), 0);
    catchConfirmBtn.textContent = kept > 0 ? `Keep ${kept}` : "Throw all back";
  }
}

/** Commit the current choice: bank the kept types, throw the rest back. */
function finishCatch(keep: ItemType[]): void {
  sendKeepCatch(keep);
  catchGroups = [];
  game.catchOpen = false;
  if (catchPanel) catchPanel.style.display = "none";
}

// ---- Action-bar slot state (combat.ts owns the bar and renders these) ------------

/** The Cast/Pull slot. While a line is out it's the pull button (the fish.reel
 *  sweep is the grab-window timer); hooked hides it (the minigame owns input);
 *  otherwise it shows whenever a rod is held. */
export function fishSlotState(): { show: boolean; enabled: boolean; label: string; icon: string } {
  if (game.fishing) {
    if (phase === "hooked") return { show: false, enabled: false, label: "", icon: "" };
    if (phase === "biting") return { show: true, enabled: true, label: "Hook!", icon: "🎣" };
    return { show: true, enabled: true, label: "Pull", icon: "🎣" };
  }
  // A focused NPC owns the bar (talk / fight) — the world-action Cast slot
  // stands down so clicking Marlow doesn't surface a rogue cast icon.
  if (!equippedRodType() || busy() || getFocus()) {
    return { show: false, enabled: false, label: "", icon: "" };
  }
  return { show: true, enabled: cachedForwardWater(rodCastRange()) !== null, label: "Cast", icon: "🎣" };
}

/** The Set Net / Collect slot: collect wins when one of our nets is in reach. */
export function netSlotState(): { show: boolean; enabled: boolean; label: string; icon: string } {
  if (game.fishing || busy() || getFocus()) {
    return { show: false, enabled: false, label: "", icon: "" };
  }
  const own = nearestOwnNet();
  if (own) {
    const left = own.readyAt - Date.now();
    if (left <= 0) return { show: true, enabled: true, label: "Collect", icon: "🕸️" };
    return { show: true, enabled: false, label: `Soaking ${Math.ceil(left / 60_000)}m`, icon: "🕸️" };
  }
  if (carriedNetType() && cachedForwardWater(FT.netRange)) {
    return { show: true, enabled: true, label: "Set Net", icon: "🕸️" };
  }
  return { show: false, enabled: false, label: "", icon: "" };
}

/** Slot 1 click (Cast / Pull) — bound here; combat.ts renders the button. */
function fishSlotAction(): void {
  if (game.fishing) { pull(); return; }
  if (busy() || !equippedRodType()) return;
  const spot = forwardWaterPoint(rodCastRange());
  if (!spot) { flashToast("No open water in reach"); return; }
  tryCastAt(spot.x, spot.z);
}

/** Net slot click (Set Net / Collect). */
function netSlotAction(): void {
  if (game.fishing || busy()) return;
  const own = nearestOwnNet();
  if (own) {
    if (Date.now() >= own.readyAt) sendCollectNet(own.id);
    return;
  }
  const type = carriedNetType();
  const spot = type ? forwardWaterPoint(FT.netRange) : null;
  if (!type || !spot) return;
  sendDeployNet(type, spot.x, spot.z);
}

// ---- Init + per-frame -------------------------------------------------------------

function showPrompt(text: string): void {
  if (!promptEl || promptShown === text) return;
  promptShown = text;
  promptEl.textContent = text;
  promptEl.style.display = text ? "block" : "none";
  promptEl.classList.toggle("urgent", text.startsWith("!"));
}

/** Wire up fishing. MUST run after initInteract and before initFocus (see the
 *  header): our pointerdown owns the click while fishing / on a valid water
 *  cast, and our Esc cancels the cast ahead of focus.ts. */
export function initFishing(s: THREE.Scene, cam: THREE.Camera, canvas: HTMLCanvasElement): void {
  scene = s;
  camera = cam;
  promptEl = document.getElementById("fishprompt");

  bobber = makeBobber();
  bobber.visible = false;
  scene.add(bobber);

  document.getElementById("fishBtn")?.addEventListener("click", fishSlotAction);
  document.getElementById("netBtn")?.addEventListener("click", netSlotAction);

  // Catch-preview HUD: keep-selected / keep-all buttons + row toggles (rows are
  // wired per-render in renderCatch).
  catchPanel = document.getElementById("catch");
  catchListEl = document.getElementById("catchList");
  catchTitleEl = catchPanel?.querySelector(".catch-title") as HTMLElement | null;
  catchConfirmBtn = document.getElementById("catchConfirm") as HTMLButtonElement | null;
  catchConfirmBtn?.addEventListener("click", () =>
    finishCatch(catchGroups.filter((g) => g.keep).map((g) => g.type)),
  );
  document.getElementById("catchKeepAll")?.addEventListener("click", () =>
    finishCatch(catchGroups.map((g) => g.type)),
  );

  // World-click cursor: water is castable with a rod out; mid-cast the whole
  // canvas is a pull surface (src/cursor.ts owns canvas.style.cursor).
  registerHoverProbe((rc) => {
    if (game.fishing) return true;
    if (busy() || !equippedRodType()) return false;
    const water = scene.getObjectByName("water");
    if (!water) return false;
    return rc.intersectObject(water, true).length > 0;
  });

  canvas.addEventListener("pointerdown", (e) => {
    if (!game.joined || e.button !== 0) return;
    if (game.fishing) {
      // Mid-cast the canvas click is ours: a click IS the pull (and never
      // re-targets focus / picks items behind the water). While hooked it's
      // swallowed inert — the minigame is keyboard-only.
      e.stopImmediatePropagation();
      pull();
      return;
    }
    if (busy() || !equippedRodType()) return;
    // Freeform cast: raycast the named water plane; a miss falls through to
    // the focus/pickup handlers untouched.
    const water = scene.getObjectByName("water");
    if (!water) return;
    ndc.x = (e.clientX / window.innerWidth) * 2 - 1;
    ndc.y = -(e.clientY / window.innerHeight) * 2 + 1;
    raycaster.setFromCamera(ndc, camera);
    const hit = raycaster.intersectObject(water, true)[0]; // water is a chunked Group now
    if (!hit) return;
    // An NPC (or interactable prop) standing in front of the water wins the
    // click — so clicking Marlow talks to him even with a rod out. Defer to
    // focus.ts (which runs right after us) rather than claiming the cast.
    if (nearestFocusableDistance(raycaster) < hit.distance) return;
    e.stopImmediatePropagation(); // rod out + clicked water: the click is ours either way
    tryCastAt(hit.point.x, hit.point.z);
  });

  addEventListener("keydown", (e) => {
    // Catch preview owns Esc while it's up — keep everything (never lose a catch
    // to a stray key), and swallow it so focus.ts/talk.ts don't also react.
    if (game.catchOpen) {
      if (e.key === "Escape") { finishCatch(catchGroups.map((g) => g.type)); e.stopImmediatePropagation(); }
      return;
    }
    if (!game.fishing) return;
    // Hooked: the minigame gets first refusal on every key (WASD lanes). Its
    // consumed keys never reach focus.ts/talk.ts — or the movement handler,
    // which is already inert under the game.fishing input lock.
    if (phase === "hooked" && minigame?.key(e)) {
      e.stopImmediatePropagation();
      return;
    }
    if (e.key === "Escape") {
      cancelCast(); // mid-fight this is giving up — the server frees the fish
      e.stopImmediatePropagation(); // focus.ts/talk.ts never see it
      return;
    }
    if (e.key === " " && phase !== "hooked") pull(); // Space = pull (jump is locked while fishing)
  });
}

// Per call — WATER_LEVEL is the ACTIVE map's live value (src/map/active.ts).
const bobBase = () => WATER_LEVEL + 0.05;

/** Per-frame: bobber + float animation, the minigame drive, the first-person
 *  prompt, and the KO bail-out. (The old cast timeout is gone — a cast
 *  legitimately waits until a fish comes; a dead socket resets via net.ts.) */
export function updateFishing(dt: number, t: number): void {
  // Deployed floats ride a gentle swell whether or not we're fishing.
  for (const g of netGroups.values()) {
    g.position.y = WATER_LEVEL + 0.04 + Math.sin(t * 1.6 + g.position.x) * 0.03;
    g.rotation.y = Math.sin(t * 0.4 + g.position.z) * 0.08;
  }
  // Other anglers' bobbers bob too (phase-offset by position so they're not in sync).
  for (const g of remoteBobbers.values()) {
    g.position.y = WATER_LEVEL + 0.05 + Math.sin(t * 2.2 + g.position.x) * 0.04;
  }

  if (!game.fishing) return;
  if (game.down) { endFishing(); return; } // the server cancelled our line with the KO

  // Keep facing the water while the line is out (movement is locked).
  const p = game.player.position;
  game.player.rotation.y = Math.atan2(castPt.x - p.x, castPt.z - p.z);

  minigame?.tick(dt); // hooked: drive the arrow lane (it judges expiries itself)
  if (tugT > 0) tugT -= dt;

  if (bobber) {
    if (phase === "biting") {
      // THE BITE: plunge under + frantic jitter until pulled or lost.
      bobber.position.y = bobBase() - 0.12 + Math.sin(t * 30) * 0.04;
    } else if (phase === "hooked") {
      // The fight: dragged around under tension.
      bobber.position.y = bobBase() - 0.08 + Math.sin(t * 14) * 0.05;
    } else if (tugT > 0) {
      // A nibble: a short, readable dip — smaller than the bite's plunge.
      bobber.position.y = bobBase() - 0.05 + Math.sin(t * 18) * 0.02;
    } else {
      bobber.position.y = bobBase() + Math.sin(t * 2.2) * 0.04;
    }
  }
  // Prompts: nibble/hook set theirs in the appliers; steady-state lines here.
  if (phase === "biting") showPrompt("‼ HOOK — Space / click!");
  else if (phase === "cast" && tugT <= 0) showPrompt("… watching the bobber (Esc to stop fishing)");
}
